import { combineReducers, Reducer } from "redux";
import { produce } from "immer";

import {
    IFavoriteFormUpdatedAction,
    IFormItemInstanceCloseRestrictionsUpdatedAction,
    IFormProfileChangedAction,
    IHideFormOverlayAction,
    IHistoryFormsInfoUpdatedAction,
    ISelectedProfileChangedAction,
    ISetGroupNotificationsAsReadAction,
    ISetNotificationItemAsReadAction,
    IShowFormOverlayAction,
} from "@multiweb/reactCore/bridge";
import { EMPTY_ARRAY, EMPTY_OBJECT } from "@multiweb/reactUtils";
import { AllowedMenuItemCodes, IMenuItemData } from "@multiweb/types/contracts";
import { ProfileChangeType } from "@multiweb/types/multiweb";
import { MutableTypedObject, RequiredProp } from "@multiweb/types/react";
import { favoriteItemsModule } from "./favoriteItems";
import { navigationModule } from "./navigation";
import { openedItemsModule } from "./openedItems";
import { MenuPanelItemId, STATIC_MENU_PANEL_ITEMS } from "./panelItems";
import { searchModule } from "./search";
import {
    ActiveSubmenuId,
    FlatNotificationGroups,
    FormCodeMap,
    FormItemIdArray,
    FormItemInstanceCloseRestrictions,
    Forms,
    IAddProfileToRelationshipsAction,
    IClearLastSubmenuAction,
    ICloseSubmenusAction,
    IConcatNotificationItemsAction,
    IConcatPanelItemsAction,
    IIncreaseNotificationsPageCountAction,
    IMenuItems,
    IMenuPanelItem,
    IMenuState,
    IRemoveProfileFormRelationshipsAction,
    IRemoveProfileFromFormsAction,
    IResetNotificationItemsAction,
    IResetNotificationsPageCountAction,
    ISetActiveSectionAction,
    ISetActiveSubmenuAction,
    ISetAllowedMenuItemCodesAction,
    ISetFormItemActionRestrictionsAction,
    ISetMenuFloatTransitionActive,
    ISetMenuItemsAction,
    ISetMenuPanelCaptionsVisibilityAction,
    ISetMenuVisibilityAction,
    ISetNotificationGroupsAction,
    ISetNotificationGroupsPanelItemsAction,
    ISetProfileActionRestrictionsAction,
    MenuPanelItems,
    MenuPanelItemType,
    MenuVisibility,
    MutableFormCodeMap,
    NotificationItems,
    Notifications,
    OpenedFormHistoryInfoArray,
    ProfileFormRelationships,
    Profiles,
    Restrictions,
    SectionId,
    WindowActionsRestrictions,
} from "./types";
import { createItemIdForProfile } from "./utils";

export const forms: Reducer<Forms> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof ISetMenuItemsAction) {
        return action.payload.menuItems.items;
    } else if (action instanceof IRemoveProfileFromFormsAction) {
        return produce(state, (draft) => {
            const { profileId, formItemIds } = action.payload;
            formItemIds.forEach((formItemId) => {
                const formItem = draft[formItemId];
                if (formItem.profiles) {
                    formItem.profiles = formItem.profiles.filter((checkedProfileId) => profileId !== checkedProfileId);
                }
            });
        });
    } else {
        return state;
    }
};

export const profiles: Reducer<Profiles> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof ISetMenuItemsAction) {
        return action.payload.menuItems.profiles;
    } else if (action instanceof IFormProfileChangedAction) {
        return produce(state, (draft) => {
            const { type, profile } = action.payload;
            switch (type) {
                case ProfileChangeType.add:
                case ProfileChangeType.update:
                    draft[profile.id] = profile;
                    break;
                case ProfileChangeType.remove:
                    delete draft[profile.id];
            }
        });
    } else if (action instanceof IFavoriteFormUpdatedAction) {
        return produce(state, (draft) => {
            const { isFavorite, profile } = action.payload;
            if (isFavorite && profile?.id) {
                const { id, formCode, title, showInNavigationTree } = profile;
                draft[id] = { id, formCode, title, showInNavigationTree };
            }
        });
    } else if (action instanceof ISelectedProfileChangedAction) {
        return produce(state, (draft) => {
            const { profile } = action.payload;
            if (profile?.id) {
                draft[profile.id] = profile;
            }
        });
    } else {
        return state;
    }
};

export const rootFormItemIds: Reducer<FormItemIdArray> = (state = EMPTY_ARRAY, action) => {
    if (action instanceof ISetMenuItemsAction) {
        return action.payload.menuItems.rootFormItemIds;
    } else {
        return state;
    }
};

export const formCodeMap: Reducer<FormCodeMap> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof ISetMenuItemsAction) {
        const items = action.payload.menuItems.items;
        return Object.values(items).reduce((result, formItem) => {
            if (formItem.formCode) {
                const formItemIds = (result[formItem.formCode] || []);
                formItemIds.push(formItem.id);
                result[formItem.formCode] = formItemIds;
                return result;
            } else {
                return result;
            }
        }, {} as MutableFormCodeMap) as FormCodeMap;
    } else {
        return state;
    }
};

export const allowedMenuItemCodes: Reducer<AllowedMenuItemCodes> = (state = null, action) => {
    if (action instanceof ISetAllowedMenuItemCodesAction) {
        return action.payload.allowedMenuItemCodes;
    } else {
        return state;
    }
};

const notificationGroups: Reducer<Readonly<FlatNotificationGroups>> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof ISetNotificationGroupsAction) {
        return action.payload.notificationGroups;
    } else {
        return state;
    }
};

const notificationItems: Reducer<Readonly<NotificationItems>> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof IResetNotificationItemsAction) {
        return EMPTY_OBJECT;
    } else if (action instanceof ISetNotificationItemAsReadAction) {
        return produce(state, (draft) => {
            const { itemId, isRead } = action.payload;
            if (draft[itemId]) {
                draft[itemId].unread = !isRead;
            }
        });
    } else if (action instanceof IConcatNotificationItemsAction) {
        return {
            ...state,
            ...action.payload.notificationItems,
        };
    } else if (action instanceof ISetGroupNotificationsAsReadAction) {
        const { notificationGroupId } = action.payload;
        return produce(state, (draft) => {
            Object.values(state)
                .filter(notificationItem => notificationItem.notificationGroupId === notificationGroupId)
                .forEach((item) => {
                    draft[item.id].unread = false;
                });
        });
    } else {
        return state;
    }
};

const notificationsPageCount: Reducer<Readonly<number>> = (state = 0, action) => {
    if (action instanceof IResetNotificationsPageCountAction) {
        return 0;
    } else if (action instanceof IIncreaseNotificationsPageCountAction) {
        return state + 1;
    } else {
        return state;
    }
};

const notifications: Reducer<Readonly<Notifications>> = combineReducers({
    items: notificationItems,
    groups: notificationGroups,
    pageCount: notificationsPageCount,
});

export const historyFormsInfo: Reducer<OpenedFormHistoryInfoArray> = (state = EMPTY_ARRAY, action) => {
    if (action instanceof IHistoryFormsInfoUpdatedAction) {
        // Otáčí pořadí, protože v původní aplikaci je nejnovější položka na konci pole, ale zobrazovat se má jako první
        return [...action.payload.historyFormsInfo].reverse();
    } else if (action instanceof IRemoveProfileFromFormsAction) {
        return state.filter(historyForm => historyForm.profileId !== action.payload.profileId);
    } else {
        return state;
    }
};

export const formItemHideWindowActions: Reducer<WindowActionsRestrictions> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof ISetFormItemActionRestrictionsAction) {
        return produce(state, (draft) => {
            const { formItemId, hideWindowActions } = action.payload;
            draft[formItemId] = hideWindowActions;
        });
    } else {
        return state;
    }
};

export const profileHideWindowActions: Reducer<WindowActionsRestrictions> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof ISetProfileActionRestrictionsAction) {
        return produce(state, (draft) => {
            const { profileId, hideWindowActions } = action.payload;
            draft[profileId] = hideWindowActions;
        });
    } else {
        return state;
    }
};

export const formItemInstanceHideCloseAction: Reducer<FormItemInstanceCloseRestrictions> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof IFormItemInstanceCloseRestrictionsUpdatedAction) {
        return produce(state, (draft) => {
            const { formInstanceId, hideCloseAction } = action.payload;
            draft[formInstanceId] = hideCloseAction;
        });
    } else {
        return state;
    }
};

export const restrictions: Reducer<Restrictions> = combineReducers({
    formItemHideWindowActions,
    profileHideWindowActions,
    formItemInstanceHideCloseAction,
});

export const profileFormRelationships: Reducer<ProfileFormRelationships> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof ISetMenuItemsAction) {
        const { items, profiles } = action.payload.menuItems;
        const profileFormItemRelations = Object.values(items)
            .filter((formItem): formItem is RequiredProp<IMenuItemData, "profiles"> => !!formItem.profiles?.length)
            .reduce((result, { id: formItemId, profiles: formProfiles }) => ({
                ...result,
                ...formProfiles.reduce((profilesResult, profileId) => ({
                    ...profilesResult,
                    [createItemIdForProfile(profileId, formItemId)]: {
                        id: createItemIdForProfile(profileId, formItemId),
                        profileId,
                        formItemId,
                    },
                }), {}),
            }), {});
        const separateProfiles = Object.values(profiles).reduce((result, { id }) => ({
            ...result,
            [createItemIdForProfile(id)]: {
                id: createItemIdForProfile(id),
                profileId: id,
            },
        }), {});
        return {
            ...profileFormItemRelations,
            ...separateProfiles,
        };
    } else if (action instanceof IRemoveProfileFormRelationshipsAction) {
        return produce(state, (draft) => {
            const { ids } = action.payload;
            ids.forEach((id) => {
                delete draft[id];
            });
        });
    } else if (action instanceof IAddProfileToRelationshipsAction) {
        return produce(state, (draft) => {
            const { profileId } = action.payload;
            const id = createItemIdForProfile(profileId);
            draft[id] = {
                id,
                profileId,
            };
        });
    } else if (action instanceof IFavoriteFormUpdatedAction) {
        return produce(state, (draft) => {
            const { menuId, profile } = action.payload;
            if (profile) {
                const profileId = profile?.id;
                const id = createItemIdForProfile(profileId, menuId);
                draft[id] = {
                    id,
                    profileId,
                };
            }
        });
    } else {
        return state;
    }
};

export const items: Reducer<IMenuItems> = combineReducers({
    allowedMenuItemCodes,
    forms,
    profiles,
    rootFormItemIds,
    formCodeMap,
    notifications,
    historyFormsInfo,
    restrictions,
    profileFormRelationships,
});

export const menuVisibility: Reducer<MenuVisibility> = (state = MenuVisibility.hidden, action) => {
    if (action instanceof ISetMenuVisibilityAction) {
        return action.payload.menuVisibility;
    } else {
        return state;
    }
};

export const menuPanelCaptionsVisible: Reducer<boolean> = (state = false, action) => {
    if (action instanceof ISetMenuPanelCaptionsVisibilityAction) {
        return action.payload.visible;
    } else {
        return state;
    }
};

export const activeSectionId: Reducer<SectionId> = (state = MenuPanelItemId.quickAccess, action) => {
    if (action instanceof ISetActiveSectionAction) {
        return action.payload.activeSectionId;
    } else {
        return state;
    }
};

export const activeSubmenuId: Reducer<ActiveSubmenuId> = (state = null, action) => {
    if (action instanceof ISetActiveSubmenuAction) {
        return action.payload.submenuId;
    } else if (action instanceof ICloseSubmenusAction) {
        return null;
    } else {
        return state;
    }
};

export const panelItems: Reducer<MenuPanelItems> = (state = STATIC_MENU_PANEL_ITEMS, action) => {
    if (action instanceof IConcatPanelItemsAction) {
        return {
            ...state,
            ...action.payload.panelItems,
        };
    } else if (action instanceof ISetActiveSubmenuAction) {
        return produce(state, (draft) => {
            const { submenuId, sectionId } = action.payload;
            draft[sectionId].lastSubmenuId = submenuId;
        });
    } else if (action instanceof IClearLastSubmenuAction) {
        return produce(state, (draft) => {
            draft[action.payload.sectionId].lastSubmenuId = undefined;
        });
    } else if (action instanceof ISetGroupNotificationsAsReadAction) {
        return produce(state, (draft) => {
            if (draft[action.payload.panelItemId])
                draft[action.payload.panelItemId].highlighted = false;
        });
    } else if (action instanceof ISetNotificationGroupsPanelItemsAction) {
        return {
            ...Object.entries(state)
                .filter(([_id, panelItem]) => panelItem.type !== MenuPanelItemType.notificationGroup)
                .reduce((result, [id, panelItem]) => {
                    result[id] = panelItem;
                    return result;
                }, {} as MutableTypedObject<IMenuPanelItem>),
            ...action.payload.notificationGroupsPanelItems,
        };
    } else {
        return state;
    }
};

export const formOverlayVisible: Reducer<boolean> = (state = false, action) => {
    if (action instanceof IShowFormOverlayAction) {
        return true;
    } else if (action instanceof IHideFormOverlayAction) {
        return false;
    } else {
        return state;
    }
};

export const menuFloatTransitionActive: Reducer<boolean> = (state = false, action) => {
    if (action instanceof ISetMenuFloatTransitionActive) {
        return action.payload.menuFloatTransitionActive;
    } else {
        return state;
    }
};

/**
 * Kořenový reducer pro menu.
 */
export const reducer: Reducer<IMenuState> = combineReducers({
    items,
    menuVisibility,
    menuPanelCaptionsVisible,
    formOverlayVisible,
    activeSectionId,
    activeSubmenuId,
    panelItems,
    menuFloatTransitionActive,
    [navigationModule.NAME]: navigationModule.reducer,
    [openedItemsModule.NAME]: openedItemsModule.reducer,
    [searchModule.NAME]: searchModule.reducer,
    [favoriteItemsModule.NAME]: favoriteItemsModule.reducer,
});
